import dayjs from 'dayjs';

import { formatInvoicePaid, formatIsoToDate } from '../../../../utils';

// CP-847: Expense.statementDate is stored as a UTC ISO timestamp pinned to 00:00:00.000Z
// (73% of rows) or 23:00:00.000Z (27%) - it carries a calendar date, not a real time.
// The range therefore spans the whole UTC day for the picked dates.
//
// Do NOT reuse the createdAt encoding for this field. It converts local midnight to UTC,
// which puts the lower bound at 06:00Z; that sorts AFTER 00:00:00.000Z in DynamoDB's
// lexicographic string comparison and silently drops ~73% of matching rows. Verified
// against staging: a single-day search returned 2 of 116 rows before this was fixed.
export const buildStatementDateRange = (start, end) => {
  if (!start || !end) {
    return undefined;
  }
  const from = dayjs(start).format('YYYY-MM-DD');
  const to = dayjs(end).format('YYYY-MM-DD');
  return `["${from}T00:00:00.000Z", "${to}T23:59:59.999Z"]`;
};

export const getSearchExpensesCsvData = expenses => {
  const csvData = [
    [
      'ID',
      'Date Entered',
      'Pay Code',
      'Driver Name & Number',
      'Assignment / Contract Activity',
      'Customer Name & Number',
      'Client Branch',
      'Cost Center',
      'Expense Start Date',
      'Expense End Date',
      'Pay Week Ending',
      'Total Receipt Amount',
      'Total To Be Billed',
      'Status',
      'Updated At',
      'Statement Date',
      'Expense Report Appropriation Number',
      'Variance',
      'Receipt Amount',
      'Non Billable',
      'Invoice Date',
      'Invoice Number',
      'Invoice Paid',
      'Projected Days',
      'Daily Charge',
      'Projected Amount',
      'Total Projected Amount',
      'Paid Through',
      'Credit Card Holder Name',
      'Airline',
      'Car Rental Company',
      'Hotel',
      'Hotel Confirmation Number',
      'Hotel Phone Number',
      'Hotel City',
      'Hotel State',
    ],
  ];

  for (let i = 0; i < expenses.length; i += 1) {
    // CP-859: the export emits one row per expense ITEM, so an expense with no items used to
    // produce no row at all and vanished from the file - the table would show 160 rows and the
    // CSV contain 159, with the missing one in the middle rather than at the end, which is the
    // hardest kind of discrepancy to notice. Found on dev: expense 104415 has zero ExpenseItem
    // rows. Iterating at least once emits the expense with its item columns blank, so the export
    // matches the table. Pre-existing; unrelated to the paging change, but it is the same file
    // and the same "partial export leaves the building" risk.
    const itemCount = expenses[i]?.expenseItem?.length || 0;
    for (let j = 0; j < Math.max(itemCount, 1); j += 1) {
      csvData.push([
        `${expenses[i].id}`,
        `${formatIsoToDate(expenses[i].createdAt) || ''}`,
        `${expenses[i].code}`,
        `${expenses[i].driverNameNumber}`,
        `${expenses[i].assignmentId}`,
        `${expenses[i].customerNameNumber}`,
        `${expenses[i].branch}`,
        `${expenses[i].costCenter}`,
        `${formatIsoToDate(expenses[i].startDate) || ''}`,
        `${formatIsoToDate(expenses[i].endDate) || ''}`,
        `${formatIsoToDate(expenses[i].payWeekEnding) || ''}`,
        `${expenses[i]?.expenseItem?.reduce((acc, curr) => (curr.receiptAmount ? acc + curr.receiptAmount : acc), 0)}`,
        `${expenses[i]?.totalToBeBilled ? expenses[i]?.totalToBeBilled : '-'}`,
        `${expenses[i]?.status}`,
        `${formatIsoToDate(expenses[i]?.updatedAt) || ''}`,
        `${formatIsoToDate(expenses[i]?.statementDate) ? formatIsoToDate(expenses[i]?.statementDate) : '-'}`,
        `${expenses[i]?.expenseReportAppropriationNbr ? expenses[i]?.expenseReportAppropriationNbr : '-'}`,
        `${expenses[i]?.variance ? expenses[i]?.variance : '-'}`,
        `${expenses[i]?.expenseItem?.[j]?.receiptAmount}`,
        `${expenses[i]?.nonBillable ? expenses[i]?.nonBillable : '-'}`,
        `${formatIsoToDate(expenses[i]?.invoiceDate) ? formatIsoToDate(expenses[i]?.invoiceDate) : '-'}`,
        `${expenses[i]?.invoiceNumber ? expenses[i]?.invoiceNumber : '-'}`,
        `${formatInvoicePaid(expenses[i]?.invoicePaid) || '-'}`,
        `${expenses[i]?.expenseItem?.[j]?.projectedDays}`,
        `${expenses[i]?.expenseItem?.[j]?.dailyCharge}`,
        `${expenses[i]?.expenseItem?.[j]?.projectedAmount}`,
        `${expenses[i]?.totalProjectedAmount}`,
        `${expenses[i]?.expenseItem?.[j]?.paidThrough}`,
        `${expenses[i]?.expenseItem?.[j]?.paidThrough === 'Credit Card' ? expenses[i]?.expenseItem?.[j]?.creditCardHolderName : null}`,
        `${expenses[i]?.code?.includes('A') ? expenses[i]?.expenseItem?.[j]?.airline?.name || 'null' : ''}`,
        `${expenses[i]?.code?.includes('C') ? expenses[i]?.expenseItem?.[j]?.carRental?.name || 'null' : ''}`,
        `${expenses[i]?.code?.includes('H') ? expenses[i]?.expenseItem?.[j]?.hotel?.name || 'null' : ''}`,
        `${expenses[i]?.code?.includes('H') ? expenses[i]?.expenseItem?.[j]?.hotelConfirmationNumber || '' : ''}`,
        `${expenses[i]?.code?.includes('H') ? expenses[i]?.expenseItem?.[j]?.hotelPhoneNumber || '' : ''}`,
        `${expenses[i]?.code?.includes('H') ? expenses[i]?.expenseItem?.[j]?.hotelCity?.name || '' : ''}`,
        `${expenses[i]?.code?.includes('H') ? expenses[i]?.expenseItem?.[j]?.hotelState || '' : ''}`,
      ]);
    }
  }

  return csvData;
};
